import { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Link,
  Stack,
  Text,
  chakra,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FaLock } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import Header from '../../site/components/Header';
import SlimFooter from '../../site/components/SlimFooter';

const CFaLock = chakra(FaLock);

const ResetPasswordFormPage = ({ token }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState('');

  const toast = useToast();
  const navigate = useNavigate();

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const formBgColor = useColorModeValue('white', 'gray.800');
  const inputBgColor = useColorModeValue('gray.50', 'gray.600');
  const buttonColorScheme = useColorModeValue('teal', 'orange');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInlineError('');

    if (!token) {
      setInlineError('Missing reset token.');
      return;
    }

    if (password.length < 8) {
      const message = 'Password must be at least 8 characters.';
      setInlineError(message);
      toast({
        title: t('reset_password.error_title'),
        description: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: t('reset_password.error_title'),
        description: t('reset_password.password_mismatch'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setInlineError(t('reset_password.password_mismatch'));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post('/passwords/reset', {
        token,
        reset_password_token: token,
        password,
        password_confirmation: confirmPassword,
      });
      if (response.status === 200) {
        toast({
          title: t('reset_password.success_title'),
          description: t('reset_password.success_description'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setTimeout(() => navigate('/login'), 1000);
      }
    } catch (error) {
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        t('reset_password.error_description');

      setInlineError(serverMessage);
      toast({
        title: t('reset_password.error_title'),
        description: serverMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex direction="column" minHeight="100vh" backgroundColor={bgColor}>
      <Header />

      <Flex
        flex="1"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        textAlign="center"
      >
        {!token ? (
          <Box p="1rem" backgroundColor={formBgColor} boxShadow="md" borderRadius="8px" minW={{ base: '90%', md: '468px' }}>
            <Heading as="h1" size="md" color={textColor} mb={2}>
              Invalid reset link
            </Heading>
            <Text color={textColor} mb={3}>This password reset link is missing a token.</Text>
            <Link as={RouterLink} to="/login" color="blue.500" fontWeight="semibold">
              Back to login
            </Link>
          </Box>
        ) : (
          <>
            <Box mb={8}>
              <Heading as="h1" size="lg" color={textColor} mb={2}>
                {t('reset_password.page_title')}
              </Heading>
              <Text fontSize="md" color={textColor}>
                {t('reset_password.page_description')}
              </Text>
            </Box>

            <Stack flexDir="column" justifyContent="center" alignItems="center">
              <Box minW={{ base: '90%', md: '468px' }}>
                <form onSubmit={handleSubmit}>
                  <Stack
                    spacing={4}
                    p="1rem"
                    backgroundColor={formBgColor}
                    boxShadow="md"
                    borderRadius="8px"
                  >
                    <FormControl>
                      <FormLabel htmlFor="reset-password-new">New password</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <CFaLock color="gray.300" />
                        </InputLeftElement>
                        <Input
                          id="reset-password-new"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t('reset_password.new_password_placeholder')}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          backgroundColor={inputBgColor}
                        />
                        <InputRightElement width="4.5rem">
                          <Button
                            h="1.75rem"
                            size="sm"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label="Toggle password visibility"
                          >
                            {showPassword ? t('reset_password.hide_password') : t('reset_password.show_password')}
                          </Button>
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <FormControl>
                      <FormLabel htmlFor="reset-password-confirm">Confirm password</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <CFaLock color="gray.300" />
                        </InputLeftElement>
                        <Input
                          id="reset-password-confirm"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder={t('reset_password.confirm_password_placeholder')}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          backgroundColor={inputBgColor}
                        />
                        <InputRightElement width="4.5rem">
                          <Button
                            h="1.75rem"
                            size="sm"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            aria-label="Toggle confirm password visibility"
                          >
                            {showConfirmPassword ? t('reset_password.hide_password') : t('reset_password.show_password')}
                          </Button>
                        </InputRightElement>
                      </InputGroup>
                      <FormHelperText>{t('reset_password.password_helper')}</FormHelperText>
                    </FormControl>

                    {inlineError ? (
                      <Text color="red.500" fontSize="sm">{inlineError}</Text>
                    ) : null}

                    <Button
                      borderRadius="4px"
                      type="submit"
                      colorScheme={buttonColorScheme}
                      width="full"
                      variant="outline"
                      isLoading={isSubmitting}
                    >
                      {t('reset_password.button_text')}
                    </Button>
                  </Stack>
                </form>
              </Box>
            </Stack>
          </>
        )}
      </Flex>

      <SlimFooter />
    </Flex>
  );
};

export default ResetPasswordFormPage;
